<template>
  <div class="rich-editor" v-loading="uploading">
    <quill-editor
      ref="editor"
      v-model="inner"
      :options="editorOption"
      :disabled="disabled"
      @change="onChange"
    />
  </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import Quill from 'quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { uploadFileDirect } from '../utils/ossUpload'

// 默认字号偏小，略放大便于装修公司编辑
const Size = Quill.import('attributors/style/size')
Size.whitelist = ['12px', '14px', '16px', '18px', '24px', '32px']
Quill.register(Size, true)

export default {
  name: 'RichEditor',
  components: { quillEditor },
  props: {
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    height: { type: String, default: '360px' }
  },
  data() {
    return {
      inner: this.value || '',
      uploading: false,
      editorOption: {
        placeholder: '请输入内容，可设置标题、加粗、颜色、插入图片等',
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, false] }],
              [{ size: [false, '12px', '14px', '16px', '18px', '24px', '32px'] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ color: [] }, { background: [] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ align: [] }],
              ['link', 'image'],
              ['clean']
            ],
            handlers: {
              image: () => this.pickImage()
            }
          }
        }
      }
    }
  },
  watch: {
    value(v) {
      if (v !== this.inner) this.inner = v || ''
    }
  },
  methods: {
    onChange({ html }) {
      this.$emit('input', html === '<p><br></p>' ? '' : html)
    },
    pickImage() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = () => {
        const file = input.files && input.files[0]
        if (file) this.uploadImage(file)
      }
      input.click()
    },
    async uploadImage(file) {
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.$message.warning('图片请小于 5MB')
        return
      }
      this.uploading = true
      try {
        const body = await uploadFileDirect(file)
        const url = body && body.data && body.data.url
        if (!url) throw new Error('未返回图片地址')
        const quill = this.$refs.editor && this.$refs.editor.quill
        if (!quill) return
        const range = quill.getSelection(true)
        const index = range ? range.index : quill.getLength()
        quill.insertEmbed(index, 'image', url)
        quill.setSelection(index + 1)
      } catch (e) {
        this.$message.error(e.message || '图片上传失败')
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.rich-editor >>> .ql-toolbar.ql-snow {
  border-radius: 6px 6px 0 0;
  background: #f8fafc;
}
.rich-editor >>> .ql-container.ql-snow {
  border-radius: 0 0 6px 6px;
  min-height: 320px;
}
.rich-editor >>> .ql-editor {
  min-height: 300px;
}
</style>

<!-- Quill 自定义字号必须手写 data-value 标签，否则全是 Normal -->
<style>
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item::before {
  content: '默认';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='12px']::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='12px']::before {
  content: '12px';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='14px']::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='14px']::before {
  content: '14px';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='16px']::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='16px']::before {
  content: '16px';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='18px']::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='18px']::before {
  content: '18px';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='24px']::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='24px']::before {
  content: '24px';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='32px']::before,
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='32px']::before {
  content: '32px';
}
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='12px']::before { font-size: 12px; }
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='14px']::before { font-size: 14px; }
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='16px']::before { font-size: 16px; }
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='18px']::before { font-size: 18px; }
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='24px']::before { font-size: 24px; }
.rich-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='32px']::before { font-size: 22px; }
</style>
